import Interaction from "../../../models/interactions/Interaction";
import { IFormQuiz, QuizFeedbackSlot } from "../../../models/Quiz";
import { getFeedbackArray } from "../../../models/QuizFeedbackHelper";
import Take from "../../../models/Take";
import { findQuizFeedbackInteraction } from "./QuizFeedbackLocator";

// The list an interaction lives in: a Take's top-level interactions, or one
// (quiz, slot) feedback array. A multi-selection is only ever valid within a
// single container; the container is always derived from the primary
// selection rather than stored, so there is no second invariant to repair.
export type SelectionContainer =
  | {
      kind: "take";
      takeId: string;
      interactions: Interaction[];
    }
  | {
      kind: "quizFeedback";
      takeId: string;
      quizId: string;
      slot: QuizFeedbackSlot;
      interactions: Interaction[];
    };

// Resolution order mirrors findInteractionLocation: top-level lists first,
// then nested quiz feedback slots.
export function resolveSelectionContainer(
  takes: Take[],
  interactionId: string
): SelectionContainer | undefined {
  for (const take of takes) {
    if (
      take.interactions.some(
        (interaction) => interaction.id === interactionId
      )
    ) {
      return {
        kind: "take",
        takeId: take.id,
        interactions: take.interactions,
      };
    }
  }

  const feedbackLocation = findQuizFeedbackInteraction(takes, interactionId);
  if (!feedbackLocation) return undefined;

  const take = takes[feedbackLocation.takeIndex];
  const quiz = take.interactions[feedbackLocation.quizIndex] as IFormQuiz;
  return {
    kind: "quizFeedback",
    takeId: feedbackLocation.takeId,
    quizId: feedbackLocation.quizId,
    slot: feedbackLocation.slot,
    interactions: getFeedbackArray(quiz, feedbackLocation.slot),
  };
}

export function selectionContainersEqual(
  a: SelectionContainer | undefined,
  b: SelectionContainer | undefined
): boolean {
  if (!a || !b) return false;
  if (a.kind !== b.kind || a.takeId !== b.takeId) return false;
  if (a.kind === "quizFeedback" && b.kind === "quizFeedback") {
    return a.quizId === b.quizId && a.slot === b.slot;
  }
  return true;
}
